import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { isBefore } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';

import { CountdownTypes } from '@modules/countdown/enums/countdown-types.enum';
import { MediaStreamModel } from '@modules/media/models/media-stream.model';
import { VideoStatus } from '@modules/media/models/media-video.model';
import { StreamService } from '@modules/shared/services/stream.service';

import { AnnouncementBannerLayoutType, AnnouncementBannerModel } from '../models/announcement-banner.model';

import { AnnouncementBannerLargeComponent } from './announcement-banner-large/announcement-banner-large.component';
import { AnnouncementBannerSmallComponent } from './announcement-banner-small/announcement-banner-small.component';

@Component({
  selector: 'mb-announcement-banner',
  templateUrl: './announcement-banner.component.html',
  styleUrls: ['./announcement-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, AnnouncementBannerLargeComponent, AnnouncementBannerSmallComponent, RouterLink],
})
export class AnnouncementBannerComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private streamService = inject(StreamService);

  private stopPoll$ = new Subject<boolean>();

  public countdownTypes = CountdownTypes;
  public teaserLayout = AnnouncementBannerLayoutType;
  public activeStreamItem: {
    slug: string;
    stream: MediaStreamModel;
    links: {
      label: string;
      link: string;
      queryParams?: {
        [key: string]: any;
      };
    }[];
  } | null = null;

  @Input() data!: AnnouncementBannerModel;

  ngOnInit(): void {
    // only check for live streams on normal layout
    if (this.data.layout === AnnouncementBannerLayoutType.NORMAL) {
      // poll player data for all connected streams
      this.data.streamItems.forEach(({ slug, stream }) => {
        if (stream) {
          this.streamService
            .pollPlayerStatusForStream(stream.id, this.stopPoll$)
            .pipe(takeUntil(this.stopPoll$))
            .subscribe((playerConfig) => {
              if (playerConfig) {
                stream.setPlayerConfig(playerConfig);
              }

              if (
                playerConfig?.status === VideoStatus.LIVE &&
                isBefore(stream.startDate, Date.now()) &&
                this.activeStreamItem?.slug != slug
              ) {
                // video is live and start date is reached -> set this stream as active
                this.activeStreamItem = {
                  slug,
                  stream,
                  links: [
                    {
                      link: slug,
                      label: stream.playButtonText,
                      queryParams: {
                        video: stream.id,
                      },
                    },
                  ],
                };
                this.cdr.detectChanges();
              }

              if (
                (playerConfig?.status === VideoStatus.STOPPED || playerConfig?.status === VideoStatus.VOD) &&
                this.activeStreamItem?.slug === slug
              ) {
                // video was live and is now stopped or VOD -> unset active stream
                this.activeStreamItem = null;
                this.cdr.detectChanges();
              }
            });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.stopPoll$.next(true);
    this.stopPoll$.complete();
  }
}
